import PropTypes from 'prop-types';

import {Box, Flex, Heading, Skeleton, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {HeroBannerBackgroundMedia} from '@amblique/king-living/app/components/hero-banner-background-media';
import {normalizeHex} from '@amblique/king-living/app/utils/normalize-hex';
import {
  hasHeroBannerBackgroundMedia,
  hasHeroBannerVideo,
  HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS,
} from '@amblique/king-living/app/utils/hero-banner-video';

/**
 * Hero Banner Block (CF-04)
 * Full-viewport hero with background image/video, title overlay, and bottom action bar.
 * - Heading: Title first, then Heading Icon (Image only, no Icon reference) below.
 * - Background: image/video or Background Colour (hex) when no media.
 * - Copy colour: Text Colour (hex) for title/subtitle.
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"wrapper" | "backgroundWrapper" | "contentWrapper" | "title" | "subtitle" | "bottomBar" | "headingIcon" | "leftCta" | "rightCta"} HeroBannerBlockCF04Parts
 * @typedef {Partial<Record<HeroBannerBlockCF04Parts, SystemStyleObject>>} HeroBannerBlockCF04Styles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {HeroBannerBlockCF04Styles} [props.styles] - Component styles
 * @returns {React.ReactElement}
 */
export const HeroBannerBlockCF04 = ({component, isComplete = true, isFetching = false, styles}) => {
  const {
    backgroundMedia,
    backgroundVideoMobile,
    backgroundColour,
    headingImage,
    headingTitle,
    subtitle,
    cta,
    rightCta,
  } = component;

  const mediaFields = {backgroundMedia, backgroundVideoMobile};
  const hasVideo = hasHeroBannerVideo(mediaFields);
  const hasBackgroundMedia = hasHeroBannerBackgroundMedia(mediaFields);
  const fallbackBg = hasBackgroundMedia ? undefined : normalizeHex(backgroundColour);

  // CF-04 does not render `body` — gate on fields that actually show in the overlay.
  const hasContentSection = Boolean(headingTitle || headingImage || subtitle || cta || rightCta);
  const isMobileNaturalVideo = hasVideo && !hasContentSection;

  const wrapperClassName = [
    'hero-banner',
    hasVideo &&
    (hasContentSection ?
      'hero-banner--cf04-video-with-content' :
      'hero-banner--cf04-video-no-content')
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box className={wrapperClassName} {...styles.wrapper}>
      <Box className="hero-image-wrapper" {...styles.backgroundWrapper}>
        {hasBackgroundMedia ? (
          <HeroBannerBackgroundMedia
            backgroundMedia={backgroundMedia}
            backgroundVideoMobile={backgroundVideoMobile}
            mobileVideoOptions={isMobileNaturalVideo ? HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS : undefined}
            {...styles.backgroundMedia}
          />
        ) : fallbackBg ? (
          <Box backgroundColor={fallbackBg} height="100%" width="100%" />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
      </Box>

      <Box className="hero-content-wrapper" {...styles.contentWrapper}>
        {!isComplete && isFetching ? (
          <Skeleton height="120px" />
        ) : (
          <>
            {headingTitle && <Heading {...styles.title}>{headingTitle}</Heading>}
            {headingImage && (
              <Box {...styles.headingIcon}>
                <Component component={headingImage} />
              </Box>
            )}
            {subtitle && <Text {...styles.subtitle}>{subtitle}</Text>}
          </>
        )}

        {(cta || rightCta) && (
          <Flex {...styles.bottomBar}>
            {cta && (
              <Box {...styles.leftCta}>
                <Component component={cta} />
              </Box>
            )}
            {rightCta && (
              <Box {...styles.rightCta}>
                <Component component={rightCta} />
              </Box>
            )}
          </Flex>
        )}
      </Box>
    </Box>
  );
};

HeroBannerBlockCF04.displayName = 'HeroBannerBlockCF04';
HeroBannerBlockCF04.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
